import type { InterviewInput, InterviewRecord } from './interviews';

const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api';

export type User = { id: number; name: string; email: string };

export type Progress = {
  days: Record<string, { status: 'in_progress' | 'completed'; completedAt: string | null }>;
  tasks: Record<string, boolean>;
  activity: string[];
  stats: {
    currentStreak: number;
    longestStreak: number;
    lastActiveDate: string | null;
    xp: number;
    level: number;
    completedDays: number;
  };
  badges: string[];
};

export type ExecutionResult = {
  passed: boolean;
  status: string;
  stdout: string;
  stderr: string;
  compileOutput: string;
  message: string;
  time: string | null;
  memory: number | null;
};

let token = localStorage.getItem('iprep_token');

export function setToken(value: string | null) {
  token = value;
  if (value) localStorage.setItem('iprep_token', value);
  else localStorage.removeItem('iprep_token');
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.error || 'Something went wrong.');
  return data as T;
}

export const api = {
  register: (body: { name: string; email: string; password: string }) =>
    request<{ token: string; user: User }>('/auth/register', { method: 'POST', body: JSON.stringify(body) }),
  login: (body: { email: string; password: string }) =>
    request<{ token: string; user: User }>('/auth/login', { method: 'POST', body: JSON.stringify(body) }),
  me: () => request<{ user: User }>('/auth/me'),
  progress: () => request<Progress>('/progress'),
  toggleTask: (day: number, taskId: string, done: boolean) =>
    request<Progress>('/tasks/toggle', { method: 'POST', body: JSON.stringify({ day, taskId, done }) }),
  completeDay: (day: number) => request<Progress>(`/days/${day}/complete`, { method: 'POST', body: '{}' }),
  getNote: (day: number) => request<{ note: { body: string; updated_at: string | null } }>(`/notes/${day}`),
  saveNote: (day: number, body: string) => request<{ saved: boolean }>('/notes', { method: 'POST', body: JSON.stringify({ day, body }) }),
  interviews: () => request<{ interviews: InterviewRecord[] }>('/interviews'),
  createInterview: (body: InterviewInput) => request<{ interview: InterviewRecord }>('/interviews', { method: 'POST', body: JSON.stringify(body) }),
  updateInterview: (id: number, body: InterviewInput) => request<{ interview: InterviewRecord }>(`/interviews/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  deleteInterview: (id: number) => request<{ deleted: boolean }>(`/interviews/${id}`, { method: 'DELETE' }),
  execute: (body: { language: string; sourceCode: string; stdin: string; expectedOutput: string }) =>
    request<ExecutionResult>('/execute', { method: 'POST', body: JSON.stringify(body) }),
};

export const emptyProgress = (): Progress => ({
  days: {}, tasks: {}, activity: [],
  stats: { currentStreak: 0, longestStreak: 0, lastActiveDate: null, xp: 0, level: 1, completedDays: 0 },
  badges: [],
});
